import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link as LinkIcon, Loader2, ShoppingCart } from "lucide-react";
import { adminAuthHeader } from "@/hooks/useAdminAuth";
import { extractApiError } from "@/utils/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Generates a tracked magic link that auto-logs the lead in and lands
 * them on the /vendita/{lesson} sales page — where the CTA dispatches
 * a Stripe checkout for the specific lesson (€2 for L.2, €10 for L.3).
 *
 * Different from GenerateUnlockLinkButton (which unlocks for FREE): this
 * flow routes the user to a PAID checkout — used when a lead is warm but
 * hasn't paid yet, and the admin wants to nudge them through personal
 * WhatsApp follow-up ("Ti mando il link per la L.2 a €2, click e paghi").
 */
const LESSON_META = {
  "lezione-2": {
    label: "Genera link acquisto L.2 (€2)",
    confirm: (name) =>
      `Generare un link magic tracciato che porta ${name || "questo utente"} ` +
      "direttamente al carrello Stripe per la Lezione 2 (€2)?\n\n" +
      "Il link auto-logga l'utente, apre la landing di vendita della L.2 con " +
      "il CTA 'Sblocca la Lezione 2 (€2)' già pronto. Il link verrà copiato " +
      "negli appunti per incollarlo su WhatsApp.",
    bg: "#ecfdf5", border: "#6ee7b7", color: "#065f46",
  },
  "lezione-3": {
    label: "Genera link acquisto L.3 (€10)",
    confirm: (name) =>
      `Generare un link magic tracciato che porta ${name || "questo utente"} ` +
      "direttamente al carrello Stripe per la Lezione 3 (€10)?\n\n" +
      "Il link auto-logga l'utente e apre la landing di vendita L.3. " +
      "Verrà copiato negli appunti.",
    bg: "#eff6ff", border: "#93c5fd", color: "#1e40af",
  },
};

export default function GeneratePurchaseLinkButton({ userId, userName, lesson, onGenerated }) {
  const [loading, setLoading] = useState(false);
  const meta = LESSON_META[lesson];
  if (!meta) return null;

  const handleClick = async () => {
    if (loading) return;
    if (!window.confirm(meta.confirm(userName))) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/users/${userId}/generate-purchase-link`,
        { lesson, hours: 168 },
        { headers: adminAuthHeader() },
      );
      // Copy so the admin can paste straight into WhatsApp.
      try {
        await navigator.clipboard.writeText(data.url);
      } catch {
        window.prompt("Link generato — copialo manualmente:", data.url);
      }
      toast.success(`Link ${data.label} copiato negli appunti`, {
        description: `Scade tra 7 giorni · tracking ${data.tracking_id.slice(0, 8)}`,
      });
      onGenerated?.(data);
    } catch (err) {
      toast.error(extractApiError(err, "Errore generazione link acquisto"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      data-testid={`gen-purchase-link-${lesson}-${userId}`}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border transition disabled:opacity-50"
      style={{ background: meta.bg, borderColor: meta.border, color: meta.color }}
      title={meta.label}
    >
      {loading
        ? <><Loader2 size={12} className="animate-spin" /> Genero…</>
        : <><ShoppingCart size={12} /> {meta.label}</>}
    </button>
  );
}
